import React, { useState, useRef, useEffect } from "react";
import { MdAttachFile, MdArrowUpward } from "react-icons/md";
import { FiUser, FiMail, FiMessageSquare, FiCode } from "react-icons/fi";
import ReactMarkdown from "react-markdown";
import { API_BASE_URL } from "services/api";

const Chat = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const scrollRef = useRef(null);

  // Auto-scroll to bottom of chat
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSubmit = async (e, forcedInput = null) => {
    if (e) e.preventDefault();
    const text = forcedInput || input;
    if (!text.trim() || isGenerating) return;

    const userMessage = { role: "user", content: text.trim() };
    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    if (!forcedInput) setInput("");
    setIsGenerating(true);

    try {
      // This must be absolute. The admin app is served from
      // admin.lugakaadimovies.com, which is static hosting with no API routes,
      // so a relative path posts to the admin site itself and comes back 405.
      // Every other view already goes through services/api.js for this reason.
      const res = await fetch(`${API_BASE_URL}/api/admin/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ messages: newMessages }),
      });

      if (!res.ok) throw new Error(`Request failed with status ${res.status}`);
      const reader = res.body.getReader();
      const decoder = new TextDecoder("utf-8");

      setMessages((prev) => [...prev, { role: "assistant", content: "" }]);

      let done = false;
      while (!done) {
        const { value, done: doneReading } = await reader.read();
        done = doneReading;
        if (value) {
          const chunk = decoder.decode(value, { stream: true });
          setMessages((prev) => {
            const updated = [...prev];
            const last = { ...updated[updated.length - 1] };
            last.content += chunk;
            updated[updated.length - 1] = last;
            return updated;
          });
        }
      }
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          // Carry the reason: a bare "failed to connect" gave no way to tell a
          // sign-in problem from an outage.
          content: `**Error:** Could not reach the AI service. ${err.message}`,
        },
      ]);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div className="flex h-full w-full flex-col items-center pt-[5vh] px-4 md:px-0 max-h-[85vh]">
      
      {messages.length === 0 ? (
        <div className="flex w-full flex-col items-center flex-1 justify-center">
          {/* Avatar / Icon */}
          <div className="mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-purple-300 to-purple-600 shadow-2xl shadow-purple-500/50">
          </div>

          {/* Greeting */}
          <div className="text-center mb-10">
            <h1 className="text-3xl font-medium text-navy-700 dark:text-white md:text-5xl">
              Good Afternoon, <span className="font-bold">Jason</span>
            </h1>
            <h2 className="mt-2 text-3xl font-medium text-navy-700 dark:text-white md:text-5xl">
              What's on <span className="font-bold text-brand-500">your mind?</span>
            </h2>
          </div>
        </div>
      ) : (
        <div ref={scrollRef} className="flex-1 w-full max-w-4xl overflow-y-auto mb-6 px-4 space-y-6 scrollbar-hide">
          {messages.map((m, idx) => (
            <div key={idx} className={`flex w-full ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[85%] rounded-3xl px-6 py-4 ${m.role === 'user' ? 'bg-brand-500 text-white' : 'bg-white text-navy-700 dark:bg-navy-800 dark:text-white shadow-xl shadow-gray-200/50 dark:shadow-none border border-gray-100 dark:border-white/10'}`}>
                {m.role === 'user' ? (
                  <p className="whitespace-pre-wrap text-base">{m.content}</p>
                ) : (
                  <div className="prose prose-sm md:prose-base dark:prose-invert max-w-none">
                    <ReactMarkdown>{m.content}</ReactMarkdown>
                  </div>
                )}
              </div>
            </div>
          ))}
          {isGenerating && messages[messages.length - 1]?.role === 'user' && (
            <div className="flex w-full justify-start">
              <div className="rounded-3xl bg-white px-6 py-4 shadow-xl shadow-gray-200/50 dark:bg-navy-800 border border-gray-100 dark:border-white/10">
                <div className="flex gap-1.5 items-center h-6">
                  <div className="w-2 h-2 rounded-full bg-brand-500 animate-bounce" style={{ animationDelay: '0ms' }} />
                  <div className="w-2 h-2 rounded-full bg-brand-500 animate-bounce" style={{ animationDelay: '150ms' }} />
                  <div className="w-2 h-2 rounded-full bg-brand-500 animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Input Box Area */}
      <div className="w-full max-w-3xl shrink-0 mt-auto pb-6">
        <div className="relative flex min-h-[140px] w-full flex-col justify-between rounded-3xl border border-gray-200 bg-white p-4 shadow-xl shadow-gray-200/50 dark:border-white/10 dark:bg-navy-800 dark:shadow-none">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            className="w-full resize-none bg-transparent px-2 text-lg outline-none placeholder:text-gray-400 dark:text-white scrollbar-hide"
            rows="2"
            placeholder="Ask AI a question or make a request..."
          />

          <div className="mt-2 flex items-center justify-between">
            {/* Left Controls */}
            <div className="flex items-center gap-2">
              <button type="button" className="flex items-center gap-2 rounded-full border border-gray-200 bg-gray-50 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-100 dark:border-white/10 dark:bg-navy-900 dark:text-white dark:hover:bg-white/5">
                <MdAttachFile className="h-4 w-4" /> Attach
              </button>
              <button type="button" className="flex items-center gap-2 rounded-full border border-gray-200 bg-gray-50 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-100 dark:border-white/10 dark:bg-navy-900 dark:text-white dark:hover:bg-white/5">
                Writing Styles <span className="ml-1 text-xs">▼</span>
              </button>
            </div>

            {/* Right Controls */}
            <div className="flex items-center gap-3">
              <label className="flex cursor-pointer items-center gap-2 text-sm font-medium text-gray-700 dark:text-white mr-2">
                <div className="relative flex items-center justify-center rounded-full bg-brand-500 p-1 w-8 h-5">
                  <div className="absolute right-1 h-3 w-3 rounded-full bg-white shadow-sm"></div>
                </div>
                Citation
              </label>
              
              <button 
                onClick={(e) => handleSubmit(e)}
                disabled={!input.trim() || isGenerating}
                className="flex h-10 w-10 items-center justify-center rounded-full bg-navy-700 text-white transition hover:bg-navy-800 dark:bg-white dark:text-navy-900 dark:hover:bg-gray-100 shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <MdArrowUpward className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Prompt Suggestions */}
      {messages.length === 0 && (
        <div className="mt-8 w-full max-w-4xl px-4 md:px-0 pb-10">
          <p className="mb-4 text-xs font-bold uppercase tracking-wider text-gray-500">
            Get started with an example below
          </p>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-4">
            {[
              { text: "What's the best action movie you have?", icon: FiUser },
              { text: "List the top 5 genres on this platform", icon: FiMessageSquare },
              { text: "Which VJ has the most movies?", icon: FiCode },
              { text: "Write an email promoting our newest movies", icon: FiMail }
            ].map((s, i) => (
              <div 
                key={i}
                onClick={() => handleSubmit(null, s.text)}
                className="group flex cursor-pointer flex-col justify-between rounded-2xl bg-white p-5 shadow-sm transition hover:shadow-md dark:bg-navy-800 border border-transparent hover:border-brand-500/30 dark:hover:border-white/20 h-32"
              >
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">{s.text}</p>
                <s.icon className="h-5 w-5 text-gray-400 group-hover:text-brand-500 transition-colors" />
              </div>
            ))}
          </div>
        </div>
      )}

    </div>
  );
};

export default Chat;
